import { useState, type ReactNode } from "react";

import { cn } from "../lib/cn.js";

/**
 * A person's face, or their initials, or nothing at all — in a box that is the same size either way.
 *
 * ==========================================================================================
 * `name` IS OPTIONAL, AND THAT IS THE WHOLE DESIGN
 * ==========================================================================================
 *
 * There are four states here and they are not one state. Three of them show no picture, and only two
 * of those show initials:
 *
 *   | The row is about…                          | Renders        |
 *   | ------------------------------------------ | -------------- |
 *   | a member who has uploaded a picture         | the picture    |
 *   | a member who has not                        | their initials |
 *   | somebody who has left, whose name is stored | their initials |
 *   | nobody — joint, the system, a lost name     | nothing        |
 *
 * OMITTING `name` IS HOW A CALLER SAYS "THIS IS NOT A PERSON". A savings account owned by the
 * household prints "Joint"; a price fetched overnight prints "Daily update"; a snapshot whose
 * recorder deleted their account prints "A former member". None of those is somebody's name, and
 * feeding them in would produce initials — "AF" for "A former member" — that belong to nobody and
 * read as a person who is not there.
 *
 * `name` was required in an earlier draft. A required prop forces every call site to pass
 * *something*, and the something would have been exactly those labels. Making it optional means the
 * absence is expressible, so the correct rendering is the one that is easy to ask for.
 *
 * ==========================================================================================
 * THE BOX NEVER CHANGES SIZE
 * ==========================================================================================
 *
 * Picture, initials and nothing all occupy the identical square. A table whose rows grew or shrank
 * depending on who had uploaded a photograph would be a table that reflows as members join. The
 * "nothing" case is a spacer rather than an omission for the same reason: "Joint" should sit on the
 * same left edge as every name above it.
 *
 * ==========================================================================================
 * NO COLOUR PER PERSON
 * ==========================================================================================
 *
 * The obvious flourish — hash the name, tint the circle — is deliberately absent. `tokens.css`
 * declares no palette to draw from and says why: there is no `--success`, no `--warning`, and no set
 * of hues, because "a red/green pair invented in advance is how a chart ends up encoding meaning in
 * colour alone". A per-member colour would be the first component in this system to pick a hue
 * outside the thirteen declared tokens, and `colour-tokens-exist.test.ts` would fail it.
 *
 * One neutral surface for every fallback also means the light and dark themes need no second
 * decision: both already define `--secondary` and `--muted`.
 *
 * ==========================================================================================
 * THE IMAGE IS DECORATIVE
 * ==========================================================================================
 *
 * `alt=""`, always. Every call site renders the person's name as text beside this component — that
 * is a requirement, not a coincidence — so alt text would make a screen reader announce the name
 * twice in a row. The picture adds nothing a non-sighted reader does not already have.
 */

export interface AvatarProps {
  /**
   * Ready for an `<img src>` — today a `data:` URL. Null or absent renders the fallback, and so does
   * a `src` that fails to load.
   */
  src?: string | null;
  /**
   * The person's name, for initials.
   *
   * ABSENT MEANS "NOT A PERSON" and renders an empty box of the same size. See the table above; this
   * is not the same as passing an empty string, which is a name that happens to be blank.
   */
  name?: string;
  size?: AvatarSize;
  className?: string;
  /**
   * For the e2e suite. Lands as `data-testid` on whichever element is rendered.
   *
   * A NAMED PROP, for the reason `Table.Cell` now has one: this component spreads no rest props, so
   * a caller writing `data-testid` directly would have it silently dropped — and TypeScript would
   * not object, because it does not type-check hyphenated JSX attribute names. That is exactly how
   * `AccountList` carried a dead locator since feature 006.
   */
  testId?: string;
}

export type AvatarSize = "sm" | "md" | "lg";

/**
 * `sm` is the table row, `md` the member list, `lg` the Settings preview.
 *
 * `lg` is 6rem because the stored picture is 256px: at a 2× device pixel ratio a 96px box asks for
 * 192 device pixels and still has headroom. Going larger would show the upscale.
 */
const SIZES: Record<AvatarSize, string> = {
  sm: "size-6 text-[0.625rem]",
  md: "size-8 text-xs",
  lg: "size-24 text-2xl",
};

const BASE = "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full";

/**
 * The rim that lifts a face off the surface behind it.
 *
 * A RING RATHER THAN A BORDER, and the difference is not cosmetic. `border` is part of the box
 * model, so adding one to a `size-6` avatar leaves 22px of picture inside a 24px square — every
 * face would quietly shrink, and the initials would need re-centring. A ring is painted outside the
 * box and occupies no layout space at all, so FR-015 still holds: picture, initials and nothing all
 * take exactly the same room.
 *
 * `ring-line` and not `ring-ring`: the latter is the violet focus colour, which on a row of avatars
 * would read as five focused controls. This is the same hairline every panel and table in the
 * application draws itself with.
 *
 * NOT APPLIED TO THE EMPTY STATE. A "Joint" row must show nothing, and a bare ring is a visible
 * circle — it would look exactly like a member whose picture failed to load.
 */
const RIM = "ring-1 ring-line";

/**
 * First letter of the first word, first letter of the last word. One word gives one letter.
 *
 * Deliberately tolerant, because a display name is free text: it may be one word, may start with a
 * digit, may be an emoji, and is only guaranteed non-empty. Anything that yields no letters at all
 * falls through to the empty box rather than to a stray character — a name of "🙂" should not print
 * half an emoji, and `Array.from` rather than `split("")` is what stops it splitting a surrogate
 * pair down the middle.
 */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "";

  const first = Array.from(words[0] ?? "")[0] ?? "";
  const last = words.length > 1 ? (Array.from(words[words.length - 1] ?? "")[0] ?? "") : "";

  return (first + last).toUpperCase();
}

/**
 * The first word of a display name.
 *
 * BESIDE A FACE, THE SURNAME IS REDUNDANT. A row reading "(AL) Ada" identifies somebody as well as
 * "(AL) Ada Lovelace" does and costs a third of the width, which matters in a table where the name
 * column repeats on every line.
 *
 * The INITIALS still come from the whole name — `initialsOf` is unchanged — so the circle says "AL"
 * beside the word "Ada". That pairing is deliberate: the two letters are what disambiguate two
 * members who share a first name, and losing the surname from the initials as well would leave
 * nothing to tell them apart.
 *
 * Returns the whole string when there is no space in it, so a one-word display name is unharmed.
 */
export function firstNameOf(name: string): string {
  const trimmed = name.trim();
  const space = trimmed.indexOf(" ");
  return space === -1 ? trimmed : trimmed.slice(0, space);
}

export function Avatar({ src, name, size = "sm", className, testId }: AvatarProps): ReactNode {
  /*
    A stored picture that will not render is not hypothetical — a truncated response, a revoked
    object URL, a member whose picture was removed in another tab between the fetch and the paint.
    The browser's default is a broken-image glyph, which in a table cell reads as a defect in the
    application. Falling back to the initials keeps the row saying who it is about.
  */
  const [failed, setFailed] = useState(false);
  const initials = name === undefined ? "" : initialsOf(name);
  const showImage = src !== undefined && src !== null && src !== "" && !failed;
  const hook = testId === undefined ? {} : { "data-testid": testId };

  if (showImage) {
    return (
      <img
        {...hook}
        src={src}
        alt=""
        className={cn(BASE, RIM, SIZES[size], "object-cover", className)}
        onError={() => {
          setFailed(true);
        }}
      />
    );
  }

  /*
    No name means no person, so there is nothing to announce and nothing to draw — but the space is
    still held, so the text beside it lines up with the rows that do have a face. `aria-hidden`
    because an empty box is not content.
  */
  if (initials === "") {
    return <span {...hook} aria-hidden="true" className={cn(BASE, SIZES[size], className)} />;
  }

  return (
    <span
      {...hook}
      aria-hidden="true"
      className={cn(BASE, RIM, SIZES[size], "bg-secondary font-medium text-muted", className)}
    >
      {initials}
    </span>
  );
}
